<!--
File: Report_10_Section_wise_AADT.vue
Description: produce report showing Region/DEU-wise Condition index.
-->
<template>
  <md-card v-if="eligible">
    <md-card-header class="md-card-header-icon md-card-header-green">
      <div class="md-layout">
        <div class="md-layout-item md-small-size-100 md-size-15">
          <RegionsDropdown v-model="selectedRegion" />
        </div>
        <div class="md-layout-item md-small-size-100 md-size-15">
          <RoadsDropdown v-model="selectedRoad" :items="roadsList" :disabled='!selectedRegion' />
        </div>
        <div class="md-layout-item md-small-size-100 md-size-15">
          <YearsDropdown :label="$t('label.survey_year')" v-model="selectedYear" :items="yearsList" />
        </div>
        <div class="md-layout-item btn-row md-small-size-100">
          <md-button v-if="isBtnAllowed('PrintButton')" class="md-success" @click="print"
            :disabled="btnDisabled || emptyData">
            {{ $t('buttons.print') }}
          </md-button>
          <md-button v-if="isBtnAllowed('ExportButton')" class="md-raised md-success" @click="exportToExcel"
            :disabled="btnDisabled">
            {{ $t('buttons.excel_export') }}
          </md-button>
        </div>
      </div>
    </md-card-header>

    <md-card-content>
      <md-progress-spinner v-if="showSpinner == true" :md-diameter="70" md-mode="indeterminate" />
      <md-table class="paginated-table table-striped table-hover" :value="paginatedReport" :md-sort.sync="currentSort"
        :md-sort-order.sync="currentSortOrder" :md-sort-fn="customSort" md-fixed-header>
        <md-table-empty-state :md-label="$t('label.no_data')"
          :md-description="$t('messages.select_another_criteria')" />
        <md-table-row slot="md-table-row" slot-scope="{item}">
          <md-table-cell style="max-width: 200px;" :md-label="$t('road_network.section')"
            md-sort-by="section_description">
            {{ item.section_description }}
          </md-table-cell>
          <md-table-cell style="max-width: 200px;" :md-label="$t('label.survey_year')" md-sort-by="survey_year"
            md-numeric>
            {{ item.survey_year }}
          </md-table-cell>
          <md-table-cell :md-label="$t('traffic.aadt_a1_spc')" md-sort-by="aadt_a1_spc" md-numeric>
            {{ item.aadt_a1_spc | numFormat(0) }}
          </md-table-cell>
          <md-table-cell :md-label="$t('traffic.aadt_b1_mb')" md-sort-by="aadt_b1_mb" md-numeric>
            {{ item.aadt_b1_mb | numFormat(0) }}
          </md-table-cell>
          <md-table-cell :md-label="$t('traffic.aadt_b2_l2tr')" md-sort-by="aadt_b2_l2tr" md-numeric>
            {{ item.aadt_b2_l2tr | numFormat(0) }}
          </md-table-cell>
          <md-table-cell :md-label="$t('traffic.aadt_b3_tractors')" md-sort-by="aadt_b3_tractors" md-numeric>
            {{ item.aadt_b3_tractors | numFormat(0) }}
          </md-table-cell>
          <md-table-cell :md-label="$t('traffic.aadt_c1_m2tr')" md-sort-by="aadt_c1_m2tr" md-numeric>
            {{ item.aadt_c1_m2tr | numFormat(0) }}
          </md-table-cell>
          <md-table-cell :md-label="$t('traffic.aadt_c2_h3tr')" md-sort-by="aadt_c2_h3tr" md-numeric>
            {{ item.aadt_c2_h3tr | numFormat(0) }}
          </md-table-cell>
          <md-table-cell :md-label="$t('traffic.aadt_d1_buses')" md-sort-by="aadt_d1_buses" md-numeric>
            {{ item.aadt_d1_buses | numFormat(0) }}
          </md-table-cell>
          <md-table-cell :md-label="$t('traffic.aadt_d2_truck_tr')" md-sort-by="aadt_d2_truck_tr" md-numeric>
            {{ item.aadt_d2_truck_tr | numFormat(0) }}
          </md-table-cell>
          <md-table-cell :md-label="$t('traffic.aadt_d3_ar4tr_tr')" md-sort-by="aadt_d3_ar4tr_tr" md-numeric>
            {{ item.aadt_d3_ar4tr_tr | numFormat(0) }}
          </md-table-cell>
          <md-table-cell :md-label="$t('traffic.aadt_total')" md-sort-by="aadt_total" md-numeric>
            {{ item.aadt_total | numFormat(0) }}
          </md-table-cell>
        </md-table-row>
      </md-table>
      <!-- NEW PAGINATION BAR -->
      <div class="md-layout" style="margin-top: 16px; align-items: center; justify-content: space-between;">
        <!-- Left -->
        <div class="md-layout-item md-size-40" v-if="totalCount > 0">
          <p class="card-category">
            {{
            $t('label.showing_from_to_of_entries', {
            from: totalCount === 0 ? 0 : ((page - 1) * pageSize) + 1,
            to: Math.min(page * pageSize, totalCount),
            total: totalCount
            })
            }}
          </p>
        </div>

        <!-- Middle -->
        <div class="md-layout-item md-size-20">
          <md-field style="width: 100px;" v-if="totalCount > 5">
            <label for="pages">{{ $t('label.per_page') }}</label>
            <md-select v-model="pageSize" name="pages">
              <md-option v-for="item in [5, 10, 25, 50, 100]" :key="item" :value="item">
                {{ item }}
              </md-option>
            </md-select>
          </md-field>
        </div>

        <!-- Right -->
        <div class="md-layout-item md-size-40" style="display: flex; justify-content: flex-end;" v-if="totalCount > 5">
          <div style="display: flex; flex-wrap: nowrap; align-items: center; gap: 6px;">
            <!-- Go to Page Input -->
            <md-field style="width: 90px;" v-if="showGoToPage && totalPages > 1">
              <label for="gotopage">{{ $t('label.go_to_page') }}</label>
              <md-input v-model.number="goToPage" type="number" min="1" :max="totalPages" @keyup.enter="jumpToPage"
                @keypress="onlyAllowPositiveNumbers" @blur="handlePageBlur" placeholder="" />
              <span class="md-helper-text">1 - {{ totalPages }}</span>
            </md-field>

            <!-- Jump to First -->
            <md-button class="md-icon-button md-success md-simple" :disabled="page === 1" @click="page = 1"
              style="min-width: 32px; padding: 0 6px;">
              <md-icon style="font-size: 18px;">first_page</md-icon>
              <md-tooltip md-direction="top">Go to First Page</md-tooltip>
            </md-button>

            <!-- Jump -5 -->
            <md-button class="md-icon-button md-success md-simple" :disabled="page <= 5"
              @click="page = Math.max(1, page - 5)" style="min-width: 32px; padding: 0 6px;">
              <md-icon style="font-size: 18px;">fast_rewind</md-icon>
              <md-tooltip md-direction="top">Back 5 Pages</md-tooltip>
            </md-button>

            <!-- Green Circles -->
            <pagination v-model="page" :per-page="pageSize" :total="totalCount"
              class="pagination-no-border pagination-success hide-duplicate-pages" />

            <!-- Jump +5 -->
            <md-button class="md-icon-button md-success md-simple" :disabled="page + 5 > totalPages"
              @click="page = Math.min(totalPages, page + 5)" style="min-width: 32px; padding: 0 6px;">
              <md-icon style="font-size: 18px;">fast_forward</md-icon>
              <md-tooltip md-direction="top">Forward 5 Pages</md-tooltip>
            </md-button>

            <!-- Jump to Last -->
            <md-button class="md-icon-button md-success md-simple" :disabled="page === totalPages"
              @click="page = totalPages" style="min-width: 32px; padding: 0 6px;">
              <md-icon style="font-size: 18px;">last_page</md-icon>
              <md-tooltip md-direction="top">Go to Last Page</md-tooltip>
            </md-button>
          </div>
        </div>


      </div>

    </md-card-content>
  </md-card>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { RegionsDropdown, RoadsDropdown, YearsDropdown } from '@/pages/Components'
import { numFormat } from "@/store/helpers/format_helpers"
import { customSortMixin } from '@/mixins/customSortMixin'
import { onClose } from '@/mixins/onCloseMixin'
import permissions from "@/mixins/permissionsMixin"
import printReport from '@/mixins/printMixin'
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Pagination from '@/components/Pagination.vue'

const baseColumns = [
  { header: 'road_network.section', key: 'section_description', width: 40 },
  { header: 'label.survey_year', key: 'survey_year', width: 10, num: true, digits: 0 },
  { header: 'traffic.aadt_a1_spc', key: 'aadt_a1_spc', width: 10, num: true, digits: 0 },
  { header: 'traffic.aadt_b1_mb', key: 'aadt_b1_mb', width: 10, num: true, digits: 0 },
  { header: 'traffic.aadt_b2_l2tr', key: 'aadt_b2_l2tr', width: 10, num: true, digits: 0 },
  { header: 'traffic.aadt_b3_tractors', key: 'aadt_b3_tractors', width: 10, num: true, digits: 0 },
  { header: 'traffic.aadt_c1_m2tr', key: 'aadt_c1_m2tr', width: 10, num: true, digits: 0 },
  { header: 'traffic.aadt_c2_h3tr', key: 'aadt_c2_h3tr', width: 10, num: true, digits: 0 },
  { header: 'traffic.aadt_d1_buses', key: 'aadt_d1_buses', width: 10, num: true, digits: 0 },
  { header: 'traffic.aadt_d2_truck_tr', key: 'aadt_d2_truck_tr', width: 10, num: true, digits: 0 },
  { header: 'traffic.aadt_d3_ar4tr_tr', key: 'aadt_d3_ar4tr_tr', width: 10, num: true, digits: 0 },
  { header: 'traffic.aadt_total', key: 'aadt_total', width: 10, num: true, digits: 0 },
];

export default {
  name: 'report-section_wise_aadt',
  mixins: [permissions, customSortMixin, printReport],
  data() {
    return {
      formName: 'Report_10',
      eligible: false,

      selectedRegion: null,
      selectedRoad: null,
      selectedYear: null,
      btnDisabled: true,

      showSpinner: false,
      currentSort: 'section_description',
      currentSortOrder: 'asc',
      page: 1,
      pageSize: 10,
      goToPage: null,
      showGoToPage: true
    }
  },
  components: {
    RegionsDropdown,
    RoadsDropdown,
    YearsDropdown,
    Pagination,
  },

  async mounted() {
    // Check if we are eligible to view the form
    this.eligible = await this.checkIfScreenAllowed()
    if (!this.eligible) {
      this.onClose()
      return
    };

    // Empty the data
    await this.clearReport(10);
    await this.reloadData()
  },

  methods: {
    ...mapActions({
      clearReport: 'CLEAR_REPORT',
      loadReportData: 'REPORT_10_SECTION_WISE_AADT',
    }),

    onClose,

    async reloadData() {
      this.showSpinner = true;
      this.btnDisabled = true;
      try {
        await this.loadReportData();
      } finally {
        this.showSpinner = false;
        this.btnDisabled = false;
      }
    },

    print() {
      this.showSpinner = true
      this.printReport(this.getPrintHeader, this.getTableHeaders, this.getTableRows)
      this.showSpinner = false
    },

    fillWorkSheet(workbook, workbookName) {
      this.generateWorkSheet(workbook, workbookName, this.$t('reports.report10_title'), this.report);
    },

    async exportToExcel() {
      // Create new Excel file
      const workbook = new ExcelJS.Workbook();
      const wbName = this.$t('reports.report10_title')
      this.fillWorkSheet(workbook, wbName)

      // Save
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `${wbName}.xlsx`);
    },

    jumpToPage() {
      if (this.goToPage >= 1 && this.goToPage <= this.totalPages) {
        this.page = this.goToPage;
      } else {
        this.goToPage = ''; // Clear invalid input
      }
    },


    validateGoToPage() {
      if (this.goToPage === 0 || this.goToPage === null || this.goToPage < 1) {
        this.goToPage = 1
      } else if (this.goToPage > this.totalPages) {
        this.goToPage = this.totalPages
      }
    },

    onlyAllowPositiveNumbers(event) {
      const char = event.key;

      // Block non-numeric characters except backspace, delete, arrows
      if (!/^\d$/.test(char)) {
        event.preventDefault();
        return;
      }

      const input = event.target.value;

      // Prevent starting with '0'
      if (input === '' && char === '0') {
        event.preventDefault();
      }
    },

    resetIfEmpty() {
      if (!this.goToPage) {
        this.goToPage = null;
      }
    },

    handlePageBlur() {
      if (!this.goToPage) {
        this.goToPage = null;
        return;
      }

      const pageNum = Number(this.goToPage);
      if (pageNum >= 1 && pageNum <= this.totalPages) {
        this.page = pageNum;
      } else {
        this.goToPage = null;
      }
    }


  },

  computed: {
    ...mapGetters(['report10_filtered', 'roadsInReport10', 'yearsInReport10']),

    report() {
      this.page = 1;
      return this.report10_filtered(this.selectedRegion, this.selectedRoad, this.selectedYear)
    },
    totalCount() {
      return this.report.length;
    },
    paginatedReport() {
      const start = (this.page - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.report.slice(start, end);
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.report.length / this.pageSize));
    },

    roadsList() {
      return this.roadsInReport10(this.selectedRegion);
    },

    yearsList() {
      return this.yearsInReport10(this.selectedRegion, this.selectedRoad);
    },

    emptyData() {
      this.page = 1;
      return this.report.length == 0
    },

    getWorksheetColumns() {
      const res = baseColumns.map(item => {
        return {
          ...item,
          header: this.$t(item.header)
        }
      });
      return res
    },

    headerTitles() {
      const titleFiltersArray = [
        { condition: this.selectedRegion, column: this.report.length > 0 ? this.$t('label.from_region', { name: this.report[0].region_description }) : '' },
        { condition: this.selectedRoad, column: this.report.length > 0 ? `${this.$t('road_network.road')}: ${this.report[0].road_description}` : '' },
        { condition: this.selectedYear, column: `${this.$t('label.as_on')} ${this.selectedYear}` },
      ].filter(item => item.condition).map(item => item.column);

      return titleFiltersArray;
    },

    getPrintHeader() {
      const titleFilters = this.headerTitles.length > 0 ? `<br>(${this.headerTitles.join(', &nbsp;')})` : '';

      return `<h4 style='text-align:center'>${this.$t('reports.reports_title')}</h4>
          <h4 style='text-align:center'>
            ${this.$t('reports.report10_title')}
            ${titleFilters}
        </h4>`;
    },

    getTableRows() {
      const columns = this.getWorksheetColumns;

      return this.report.map(item => {
        const rowCells = columns.map(col => {
          let val = item[col.key];

          if (col.num) {
            // Round if digits = 0
            if (col.digits === 0) val = Math.round(val);

            // If it's a year field, force plain integer display (no commas)
            if (col.key.toLowerCase().includes('year')) return `<td>${val}</td>`;

            // Otherwise format as localized number
            return `<td class='numCell'>${numFormat(val, col.digits)}</td>`;
          }

          return `<td>${val}</td>`;
        });

        return `<tr>${rowCells.join('')}</tr>`;
      }).join('');
    },
  },

  watch: {
    selectedRegion() {
      this.selectedRoad = null
    },
    page() {
      this.goToPage = null;
    },
    pageSize() {
      this.goToPage = null;
    }
  }
}
</script>
<style lang="scss" scoped>
.md-card {
  margin: 0px 0;
}

.btn-row {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
}

::v-deep .md-table-head-label {
  font-size: 13px !important;
  overflow: visible !important;
  white-space: normal !important;
  text-align: center !important;
  word-wrap: break-word !important;
}

.paginated-table table>tbody>tr>td {
  font-size: 12px !important;
  height: 36px;
  padding: 0px !important;
  padding-right: 24px !important;
}

.md-progress-spinner {
  margin: 18px;
  position: absolute;
  top: 25%;
  left: 45%;
  z-index: 20;
}

/* Active page: Green circle */
::v-deep .pagination-success .page-item.active .page-link {
  border-radius: 50%;
  color: green;
  border: 1px solid green;
  margin: 0 2px;
  min-width: 32px;
  text-align: center;
}

/* Non-active pages: just plain text */
::v-deep .pagination-success .page-item:not(.active) .page-link {
  border-radius: 50%;
  color: green;
  border: 1px solid white;
  margin: 0 2px;
  min-width: 32px;
  text-align: center;
}

/* Remove duplicate first/last page circles from pagination component */
::v-deep .hide-duplicate-pages .page-item:first-of-type,
::v-deep .hide-duplicate-pages .page-item:last-of-type {
  display: none !important;
}

::v-deep .md-field input {
  padding: 4px !important;
  text-align: center;
}

::v-deep input[type="number"] {
  text-align: left;
}

/* Chrome, Safari, Edge */
::v-deep input[type="number"]::-webkit-inner-spin-button,
::v-deep input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: auto;
  margin: 0;
}

/* Firefox */
::v-deep input[type="number"] {
  -moz-appearance: textfield;
}

</style>